/**
 * P-Zone — the Dynamic Profit Zone, as a chart REFERENCE (2026-10-07).
 *
 * WHAT IT IS. Two levels beyond the higher-timeframe structure, in the direction
 * the HTF Band is moving:
 *     Zone 1 = anchor + direction × m1 × scale      (default m1 = 2.5)
 *     Zone 2 = anchor + direction × m2 × scale      (default m2 = 4)
 * anchor    = the HTF Band's trade-side edge (rising: its top, falling: its
 *             bottom) of the last CLOSED higher-timeframe bar
 * scale     = the mean true range of the last 50 chart bars that had closed by
 *             that HTF bar's close
 * direction = rising when BOTH band lines stepped up from the previous HTF bar,
 *             falling when both stepped down, otherwise NEUTRAL (no zones).
 *
 * WHAT IT IS NOT. A tested profit-booking signal. The pre-registered study
 * (docs/EXIT-ARCHITECTURE-STUDY.md) found booking a partial here was no better
 * than booking at a random profitable moment, and a Zone 1 touch did not
 * reliably mean the move was tiring. The owner asked for it as a visual
 * reference; nothing that decides (scans, strategies, alerts, orders) reads it.
 *
 * AS KNOWN, NEVER REPAINTED. Every chart bar uses only the HTF bar that had
 * CLOSED by that bar's OPEN, and only chart bars that had closed by then. So the
 * zones hold flat for a whole HTF period and never move inside it, history shows
 * the zones exactly as they were known then, and a still-forming (or stale)
 * fetched HTF bar can never change them. Consequence, visible on the chart: each
 * P-Zone step sits ONE HTF period to the right of the HTF Band step it came from
 * (the HTF Band chip draws each HTF body across its own period, a level known
 * only once that period has closed).
 *
 * Same arithmetic as the study's buildZoneCtx (backend/src/scripts/exitstudy),
 * asserted bar for bar by `npm run verify:profit-zone`.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { computeClnBars } from './clnCandles';
import { htfBarCloseTime } from './oscHtf';

export const PZ_DEFAULT_M1 = 2.5;
export const PZ_DEFAULT_M2 = 4;
export const PZ_ATR_LEN = 50;

export interface ProfitZonePoint {
  /** The chart bar's time. */
  time: number;
  /** +1 rising HTF Band (zones above price), -1 falling (zones below). */
  dir: 1 | -1;
  z1: number;
  z2: number;
  anchor: number;
  scale: number;
  /** Open time of the closed HTF bar the zones were set from. */
  htfTime: number;
}

/** Why a bar has no zone — so the legend can say which. */
export type ProfitZoneGap = 'neutral' | 'warmup' | 'noHtf';

export interface ProfitZoneResult {
  /** One entry per chart bar, in order: a zone, or why there is none. */
  points: Array<ProfitZonePoint | ProfitZoneGap>;
}

export function computeProfitZones(
  bars: CandleRow[],
  barMinutes: number,
  htf: CandleRow[],
  htfMinutes: number,
  opts: { clnLength: number; m1?: number; m2?: number },
): ProfitZoneResult {
  const n = bars ? bars.length : 0;
  const points: ProfitZoneResult['points'] = new Array(n).fill('noHtf');
  if (!n || !htf || htf.length < 2 || !(htfMinutes > barMinutes) || !(barMinutes > 0)) return { points };
  const m1 = Number.isFinite(opts.m1) ? Number(opts.m1) : PZ_DEFAULT_M1;
  const m2 = Number.isFinite(opts.m2) ? Number(opts.m2) : PZ_DEFAULT_M2;
  const len = Math.max(2, Math.round(opts.clnLength) || 50);
  const cln = computeClnBars(htf, len);
  const barSec = barMinutes * 60;
  const t = bars.map(b => b.time as number);

  // True range and its trailing mean (simple, PZ_ATR_LEN bars, ending at each bar).
  const meanTr = new Float64Array(n).fill(NaN);
  let s = 0;
  const tr = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const b = bars[i];
    tr[i] = i === 0 ? b.high - b.low
      : Math.max(b.high - b.low, Math.abs(b.high - bars[i - 1].close), Math.abs(b.low - bars[i - 1].close));
    s += tr[i];
    if (i >= PZ_ATR_LEN) s -= tr[i - PZ_ATR_LEN];
    if (i >= PZ_ATR_LEN - 1) meanTr[i] = s / PZ_ATR_LEN;
  }
  const close = (k: number) => htfBarCloseTime(htf[k].time as number, htfMinutes);
  // For each HTF bar: the last chart bar that had closed by its close.
  const jOf = new Int32Array(htf.length).fill(-1);
  { let j = -1; for (let k = 0; k < htf.length; k++) { while (j + 1 < n && t[j + 1] + barSec <= close(k)) j++; jOf[k] = j; } }

  let k = -1;
  for (let i = 0; i < n; i++) {
    while (k + 1 < htf.length && close(k + 1) <= t[i]) k++;       // closed by THIS bar's open
    if (k < 0) continue;                                          // 'noHtf' until an HTF bar has closed
    const a = cln[k], p = cln[k - 1];
    if (k < len || !a || !p) { points[i] = 'warmup'; continue; }
    const j = jOf[k];
    if (j < PZ_ATR_LEN - 1 || !isFinite(meanTr[j])) { points[i] = 'warmup'; continue; }
    const up = a.lrOpen > p.lrOpen && a.lrClose > p.lrClose;
    const dn = a.lrOpen < p.lrOpen && a.lrClose < p.lrClose;
    if (!up && !dn) { points[i] = 'neutral'; continue; }
    const dir: 1 | -1 = up ? 1 : -1;
    const anchor = up ? Math.max(a.lrOpen, a.lrClose) : Math.min(a.lrOpen, a.lrClose);
    const scale = meanTr[j];
    points[i] = { time: t[i], dir, z1: anchor + dir * m1 * scale, z2: anchor + dir * m2 * scale, anchor, scale, htfTime: htf[k].time as number };
  }
  return { points };
}
